import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Pen, Image, Film, Download, Play } from 'lucide-react';

// Each step renders a pre-rendered scene video on loop. The scenes live as
// MP4 + WebM at /reel/scenes/<slug>.{mp4,webm} (compiled from the live React
// reel via apps/web/scripts/render-reel-scenes.mjs). Re-run that script
// after editing scene_*.jsx to refresh the videos.
//   step1 Write    -> describe
//   step2 Generate -> objects
//   step3 Animate  -> wire
//   step4 Ship     -> export
// Build and Media scenes feed LandingFeatures; hero-reel anchors LandingHero.
const steps = [
    {
        icon: Pen,
        label: 'Write',
        title: 'Describe the madness',
        desc: 'Type what you see in your brain. A neon samurai riding a cloud? Sure. Helix gets it.',
        color: 'var(--primary-color)',
        slug: 'describe',
    },
    {
        icon: Image,
        label: 'Generate',
        title: 'Build your cast',
        desc: 'Forge characters, locations, and styles in the Object library. Reuse them anywhere with a single @mention.',
        color: '#a855f7',
        slug: 'objects',
    },
    {
        icon: Film,
        label: 'Animate',
        title: 'Wire the storyboard',
        desc: 'Connect scenes on the node canvas. Continue transitions carry motion across cuts so your shots actually flow.',
        color: '#10b981',
        slug: 'wire',
    },
    {
        icon: Download,
        label: 'Ship',
        title: 'Export and flex',
        desc: 'Render production-ready video, drop it on social, and watch the world react in real time.',
        color: '#f59e0b',
        slug: 'export',
    },
];

// Shared scene video. MP4 listed FIRST so iOS Safari picks the H.264 file
// without ever evaluating the WebM source — some iOS Safari versions
// crash autoplay if the first source has an unsupported codec, even
// though the spec says they should skip. WebM is the bandwidth-saving
// fallback for Chrome / Firefox / modern desktop Safari.
//
// muted + playsInline + autoPlay is the only combo iOS will autoplay.
//
// Failure modes we handle:
//   1. Framer-motion opacity:0 at mount → IntersectionObserver retries play
//      when the element enters the viewport.
//   2. iOS Low Power Mode / Safari autoplay-disabled / .play() rejects →
//      `autoplayFailed` flips true, a tap-to-play overlay renders. The
//      tap is a user-gesture, which satisfies every browser's autoplay
//      gate, including LPM and "Never autoplay" Safari settings.
//
// The poster jpg paints immediately while the video downloads, so even if
// the user never taps, they see the first frame as a still.
export const SceneVideo = ({ slug, className = '', ariaLabel }) => {
    const ref = useRef(null);
    const [autoplayFailed, setAutoplayFailed] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const tryPlay = () => {
            el.play()
                .then(() => setAutoplayFailed(false))
                .catch(() => setAutoplayFailed(true));
        };
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) tryPlay(); },
            { threshold: 0.25 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const handleTap = () => {
        const el = ref.current;
        if (!el) return;
        el.play().then(() => setAutoplayFailed(false)).catch(() => {});
    };
    return (
        <div className={`relative w-full aspect-video block ${className}`}>
            <video
                ref={ref}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                poster={`/reel/scenes/${slug}.jpg`}
                aria-label={ariaLabel || `Helix scene - ${slug}`}
                className="absolute inset-0 w-full h-full"
            >
                <source src={`/reel/scenes/${slug}.mp4`} type="video/mp4" />
                <source src={`/reel/scenes/${slug}.webm`} type="video/webm" />
            </video>
            {autoplayFailed && (
                <button
                    type="button"
                    onClick={handleTap}
                    aria-label="Play scene"
                    className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                >
                    <div className="w-14 h-14 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-lg group-active:scale-95 transition-all">
                        <Play size={24} className="text-black ml-0.5" fill="currentColor" />
                    </div>
                </button>
            )}
        </div>
    );
};

const LandingDemo = () => {
    return (
        <section id="demo" className="relative py-24 px-6 bg-[rgba(0,0,0,0.3)]">
            {/* Ambient */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-[var(--primary-color)] opacity-[0.04] blur-[100px] rounded-full" />
            </div>

            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block text-[var(--primary-color)] text-xs font-bold uppercase tracking-widest mb-4">How It Works</span>
                    <h2 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-white">
                        Idea to export. Four steps. No excuses.
                    </h2>
                    <p className="mt-4 text-lg text-[var(--text-secondary)] max-w-xl mx-auto">
                        You bring the vision. Helix does the heavy lifting, the rendering, and the coffee runs.
                    </p>
                </motion.div>

                {/* Staggered node-graph layout — each step renders as a row
                     with the scene preview on one side and the copy on the
                     other, sides alternating. A vertical "wire" runs down
                     the center on desktop with branch lines into each card;
                     on mobile it collapses to a single column with the
                     scene above the offset copy block (no center wire, but
                     each card carries a left-edge accent so the rhythm
                     reads consistently across breakpoints). */}
                <div className="relative">
                    {/* Center wire — desktop only. Soft cyan glow tracing the
                         spine of the node graph. Hidden on small screens
                         where the layout is single-column. */}
                    <div
                        aria-hidden
                        className="hidden md:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px"
                        style={{
                            background:
                                'linear-gradient(180deg, transparent 0%, rgba(24,181,240,0.35) 8%, rgba(168,85,247,0.35) 50%, rgba(245,158,11,0.35) 92%, transparent 100%)',
                        }}
                    />

                    <div className="flex flex-col gap-20 md:gap-28">
                        {steps.map((step, i) => {
                            const sceneOnLeft = i % 2 === 0;
                            return (
                                <motion.div
                                    key={step.label}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-80px' }}
                                    transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                                    className="relative grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-16"
                                >
                                    {/* Center node — sits on the wire on desktop,
                                         marks the row's slot on mobile. */}
                                    <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center">
                                        <div
                                            className="w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md"
                                            style={{
                                                background: `radial-gradient(circle, ${step.color}30 0%, ${step.color}08 60%, transparent 100%)`,
                                                boxShadow: `0 0 24px ${step.color}66, inset 0 0 0 1px ${step.color}55`,
                                            }}
                                        >
                                            <step.icon size={22} style={{ color: step.color }} />
                                        </div>
                                    </div>

                                    {/* Scene preview — bigger than the old grid
                                         tiles. Order swaps every other row so the
                                         layout staggers around the center wire. */}
                                    <div
                                        className={`relative w-full ${sceneOnLeft ? 'md:order-1' : 'md:order-2'}`}
                                    >
                                        {/* Branch wire from center → preview edge.
                                             Direction flips with the row. */}
                                        <div
                                            aria-hidden
                                            className={`hidden md:block absolute top-1/2 -translate-y-1/2 h-px ${
                                                sceneOnLeft ? 'right-0 left-auto' : 'left-0 right-auto'
                                            }`}
                                            style={{
                                                width: 'calc(50% + 2rem)',
                                                background: sceneOnLeft
                                                    ? `linear-gradient(90deg, transparent 0%, ${step.color}88 100%)`
                                                    : `linear-gradient(90deg, ${step.color}88 0%, transparent 100%)`,
                                            }}
                                        />

                                        <div
                                            className="relative rounded-2xl overflow-hidden ring-1 ring-white/8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
                                            style={{ boxShadow: `0 0 0 1px ${step.color}25, 0 30px 80px -30px ${step.color}33` }}
                                        >
                                            <SceneVideo
                                                slug={step.slug}
                                                ariaLabel={`${step.label} step animation`}
                                            />
                                        </div>
                                    </div>

                                    {/* Copy block — offset (not centered) so the
                                         rhythm feels editorial rather than
                                         clinical. Aligns toward the center wire
                                         on desktop, full-width on mobile. */}
                                    <div
                                        className={`relative ${
                                            sceneOnLeft
                                                ? 'md:order-2 md:pl-10 md:text-left'
                                                : 'md:order-1 md:pr-10 md:text-right'
                                        }`}
                                    >
                                        {/* Mobile-only left accent — replaces the
                                             desktop center wire so each card still
                                             has an obvious "node" treatment. */}
                                        <div
                                            aria-hidden
                                            className="md:hidden absolute -left-4 top-0 bottom-0 w-px"
                                            style={{
                                                background: `linear-gradient(180deg, transparent, ${step.color}88 50%, transparent)`,
                                            }}
                                        />

                                        <span
                                            className="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.18em]"
                                            style={{
                                                color: step.color,
                                                background: `${step.color}14`,
                                                boxShadow: `inset 0 0 0 1px ${step.color}40`,
                                            }}
                                        >
                                            Step {String(i + 1).padStart(2, '0')} · {step.label}
                                        </span>
                                        <h3 className="mt-4 text-3xl md:text-4xl font-heading font-black tracking-tight text-white leading-[1.1]">
                                            {step.title}
                                        </h3>
                                        <p className="mt-4 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed max-w-md md:max-w-none md:inline-block">
                                            {step.desc}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LandingDemo;
